import { CartLinesUpdateEvent, CartNoteUpdateEvent, CartViewEvent, CartErrorEvent } from '@theme/standard-events';

class CartItems extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.isCartPage = !!document.body.classList.contains('template-cart');

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    window.addEventListener('quantity-change', this.debouncedOnChange);

    // Shopify standard event: cart is viewed as a page (the drawer variant dispatches from cart-drawer.js)
    if (this.isCartPage) {
      const dispatchCartView = () => {
        // Fetched via the AJAX API and converted, because the Shopify.actions.getCart()
        // cart shape does not pass the standard-events payload validation
        fetch(`${routes.cart_url}.js`)
          .then(response => response.json())
          .then((ajaxCart) => {
            this.dispatchEvent(
              new CartViewEvent({
                context: 'page',
                cart: CartViewEvent.createCartFromAjaxResponse(ajaxCart),
              }),
            );
          })
          .catch(() => {});
      };

      // Wait for DOMContentLoaded so app scripts have time to attach their listeners
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', dispatchCartView, { once: true });
      }
      else {
        dispatchCartView();
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('quantity-change', this.debouncedOnChange);
  }

  onChange(event) {
    if (!event.target.closest('[data-cart-item]')) return;

    const { key, quantity } = event.detail;
    this.updateQuantity(key, quantity, event.target.closest('[data-cart-item]'));
  }

  async updateQuantity(key, quantity, el) {
    const body = JSON.stringify({
      id: key,
      quantity,
    });

    // Shopify standard event, dispatched before the fetch; the cart section renderer
    // listens for it and re-renders all [data-cart-section] markers when it settles
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: +quantity === 0 ? 'remove' : 'update',
        context: this.isCartPage ? 'cart' : 'dialog',
        lines: [
          {
            id: key,
            quantity: +quantity,
          },
        ],
        promise: deferred.promise,
      }),
    );

    try {
      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        ...{ body },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        debug() && console.error(errorBody);

        throw new Error(response.status);
      }

      const parsedState = await response.json();

      // Resolving the promise hands rendering over to the cart section renderer
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedState),
      });

      if (+quantity === 0) {
        // Collapse animation while the renderer fetches the new sections
        el.querySelector('cart-remove-button')?.classList.add('hidden');
        el.querySelector('[data-cart-item-wrapper]').style.gridTemplateRows = '0fr';
      }
      else {
        // Restore focus to the quantity input once the renderer swapped in the new DOM
        const id = key.split(':')[0];
        this.afterCartSectionsRendered(() => {
          document.getElementById(`Quantity-${id}`)?.focus();
        });
      }
    }
    catch (error) {
      debug() && console.error('Error updating the item.', error);

      const is422 = error.toString().includes('422');
      this.dispatchEvent(
        new CartErrorEvent({
          error: window.cartStrings.error,
          code: is422 ? 'INVALID' : 'SERVICE_UNAVAILABLE',
        }),
      );
      deferred.reject(error);

      // The renderer re-syncs the cart state after a rejection (the mutation may have
      // partially succeeded); write the error feedback into the freshly rendered DOM
      this.afterCartSectionsRendered(() => {
        const cartErrors = document.getElementById('cart-errors');
        if (cartErrors) cartErrors.textContent = window.cartStrings.error;

        if (is422) this.showLineItemError(key.split(':')[0]);
      });
    }
  }

  // Runs the callback after the cart section renderer finished, with a timeout fallback
  // so feedback never silently disappears when the render itself fails
  afterCartSectionsRendered(callback) {
    let handled = false;
    const handler = () => {
      handled = true;
      callback();
    };

    document.addEventListener('cart-sections:rendered', handler, { once: true });
    setTimeout(() => {
      if (!handled) {
        document.removeEventListener('cart-sections:rendered', handler);
        callback();
      }
    }, 3000);
  }

  // Show error text for the line item; the freshly rendered input already shows the server quantity
  showLineItemError(id) {
    const quantity = document.getElementById(`Quantity-${id}`)?.value;
    const lineItemError = document.getElementById(`Line-item-error-${id}`);
    if (lineItemError && quantity !== undefined) {
      lineItemError.querySelector('.cart-item__error-text').innerHTML = window.cartStrings.quantityError.replace('[quantity]', quantity);
    }

    const cartStatus = document.getElementById('cart-live-region-text');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }
}
if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();

      this.closest('cart-items').updateQuantity(event.currentTarget.dataset.cartItemKey, 0, event.target.closest('[data-cart-item]'));
    });
  }
}
if (!customElements.get('cart-remove-button')) {
  customElements.define('cart-remove-button', CartRemoveButton);
}

class CartNote extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', debounce((event) => {
      const body = JSON.stringify( {
        note: event.target.value,
      });

      // Shopify standard event, dispatched before the fetch so listeners can show loading states
      const deferred = CartNoteUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartNoteUpdateEvent({
          context: document.body.classList.contains('template-cart') ? 'cart' : 'dialog',
          note: event.target.value,
          promise: deferred.promise,
        }),
      );

      fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        ...{
          body,
        },
      })
        .then(response => response.json())
        .then((ajaxCart) => {
          deferred.resolve({
            cart: CartNoteUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          });
        })
        .catch(error => deferred.reject(error));
    }, 300),
    );
  }
}
if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}
